import { useCallback, useState } from 'react'
import { authedFetch } from './auth'

/**
 * Submitting a game to RPGreek for shelving.
 *
 * There is no shelved path. `verdict` is always a deflection — the Weight
 * Council never issues a usable number — and this hook models that honestly
 * rather than inventing a `weight` field that would just invite a caller to
 * branch on it later. Same shape as Regretsit's useSubmit, deliberately: two
 * portals, two different reasons the answer is always no.
 *
 * Requires a real, currently-valid Atlas session — the endpoint rejects
 * unauthenticated calls outright (see netlify/functions/shelve.ts). `loginRequired`
 * is a distinct state from `error` so the UI can re-open the auth panel instead
 * of just showing a dead end.
 */
export interface ShelveState {
  pending: boolean
  verdict: string | null
  /** Set when the Council answered from its fallback rather than the model. */
  degraded: boolean
  error: string | null
  loginRequired: boolean
}

const INITIAL: ShelveState = { pending: false, verdict: null, degraded: false, error: null, loginRequired: false }

export function useShelve() {
  const [state, setState] = useState<ShelveState>(INITIAL)

  const submit = useCallback(async (title: string, body: string) => {
    if (!body.trim()) return
    setState({ ...INITIAL, pending: true })
    try {
      const res = await authedFetch('/api/shelve', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ title, body }),
      })
      const data = (await res.json().catch(() => null)) as
        | { verdict?: string; degraded?: boolean; error?: string }
        | null

      if (res.status === 401 || data?.error === 'login_required') {
        setState({ ...INITIAL, loginRequired: true })
        return
      }

      if (!res.ok || !data?.verdict) {
        setState({ ...INITIAL, error: 'The Council is backlogged reviewing other submissions. Try again shortly.' })
        return
      }

      setState({ ...INITIAL, verdict: data.verdict, degraded: !!data.degraded })
    } catch (e) {
      if (e instanceof Error && e.message === 'not_authenticated') {
        setState({ ...INITIAL, loginRequired: true })
        return
      }
      setState({ ...INITIAL, error: 'The Council could not be reached.' })
    }
  }, [])

  const dismiss = useCallback(() => setState(INITIAL), [])

  return { ...state, submit, dismiss }
}
